import { useEffect, useState } from 'react';
import { Users, PhoneCall, PhoneOff, Loader2, UserPlus } from 'lucide-react';
import { partnersAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// SALES PARTNERS — who may dial prospects from the sales portal (sales.alfredvoice.com).
//
// The portal registers a partner here the first time they open its dialer, DISABLED.
// This card is where a platform owner opens (or closes) their line. It is the control:
// the portal's own training gate reads config its client writes to itself, so core
// refuses every partner dial until this flag is on — whatever the portal shows.
//
// Owner-only, server-enforced (PATCH /partners/:id is refused for a tenant admin), and
// every flip is audited as partner.line_opened / partner.line_closed.

export default function PartnersCard() {
  const { tenant, selectedTenant, isOwner } = useAuth();
  const shownTenant = (isOwner && selectedTenant) || tenant;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ email: '', name: '', phone: '' });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    partnersAPI.list()
      .then((r) => { if (!cancelled) setRows(r?.data || []); })
      .catch((err) => { if (!cancelled) setError(err?.status === 403 ? '' : err.message || 'Failed to load partners.'); })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [shownTenant?.id]);

  if (!isOwner) return null;

  const flip = async (p) => {
    setError('');
    setBusyId(p.id);
    try {
      const r = await partnersAPI.update(p.id, { dialEnabled: !p.dialEnabled });
      setRows((cur) => cur.map((x) => (x.id === p.id ? r.data : x)));
    } catch (err) {
      setError(err?.body?.message || err.message || 'Could not change the line.');
    } finally {
      setBusyId(null);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setError('');
    const email = draft.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError('Enter the partner\'s email address.'); return; }
    const digits = draft.phone.replace(/[^0-9+]/g, '');
    const phone = !digits ? undefined
      : digits.startsWith('+') ? digits
        : /^\d{10}$/.test(digits) ? `+1${digits}`
          : /^1\d{10}$/.test(digits) ? `+${digits}` : null;
    if (phone === null) { setError('Phone must be 10 digits, or +… for anywhere else.'); return; }
    setAdding(true);
    try {
      const r = await partnersAPI.create({ email, ...(draft.name.trim() ? { name: draft.name.trim() } : {}), ...(phone ? { phone } : {}) });
      setRows((cur) => (cur.some((x) => x.id === r.data.id) ? cur.map((x) => (x.id === r.data.id ? r.data : x)) : [...cur, r.data]));
      setDraft({ email: '', name: '', phone: '' });
    } catch (err) {
      setError(err?.body?.message || err.message || 'Could not add the partner.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <section className="card mb-8">
      <div className="flex items-center gap-2 mb-1">
        <Users size={18} className="text-alfred-gold" />
        <h2 className="text-lg font-medium text-stone-100">Sales partners</h2>
      </div>
      <p className="text-sm text-stone-400 mb-4">
        People who dial prospects from the sales portal, in their own voice, on this account's
        sales line. A partner appears here after they first open the portal's dialer. Their
        line is closed until you open it, no matter what the portal shows them.
      </p>
      {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-stone-500">No partners yet. One appears the first time someone opens the portal's dialer.</p>
      ) : (
        <ul className="divide-y divide-stone-800">
          {rows.map((p) => (
            <li key={p.id} className="py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-stone-100 truncate">{p.name || p.email}</p>
                <p className="text-xs text-stone-500 truncate">
                  {p.name ? `${p.email} · ` : ''}
                  {p.certifiedAt ? `certified ${new Date(p.certifiedAt).toLocaleDateString()}` : 'not certified by the portal'}
                  {' · '}
                  <span className={p.dialEnabled ? 'text-emerald-500' : 'text-stone-400'}>
                    {p.dialEnabled ? 'line open' : 'line closed'}
                  </span>
                </p>
              </div>
              <button
                type="button"
                className={p.dialEnabled ? 'btn-ghost shrink-0' : 'btn-primary shrink-0'}
                onClick={() => flip(p)}
                disabled={busyId === p.id}
              >
                {busyId === p.id
                  ? <Loader2 size={16} className="animate-spin" />
                  : p.dialEnabled ? <PhoneOff size={16} /> : <PhoneCall size={16} />}
                {p.dialEnabled ? 'Close line' : 'Open line'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add ahead of their first sign-in. They still need to be on the portal's own
          sign-in allowlist (ALLOWED_EMAILS on its backend) to get in at all. */}
      <form onSubmit={add} className="mt-4 pt-4 border-t border-stone-800">
        <p className="text-xs text-stone-500 mb-2">
          Add a partner before they sign in. They also need to be on the portal's sign-in list.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input className="field-input" placeholder="email" type="email" value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })} disabled={adding} />
          <input className="field-input" placeholder="name" value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })} disabled={adding} />
          <input className="field-input" placeholder="phone (optional)" inputMode="tel" value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })} disabled={adding} />
          <button type="submit" className="btn-primary shrink-0" disabled={adding || !draft.email.trim()}>
            {adding ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Add
          </button>
        </div>
      </form>
    </section>
  );
}
